import React, { useEffect, useRef, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import ClientOnboarding from "../../shared/createForms/ClientOnboarding";
import ZoneManagement from "../../shared/createForms/ZoneManagement";
import GlobalButton from "../../shared/utils/GlobalButton";

export default function AddClient() {
  const serverURL = process.env.REACT_APP_URL;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const location = useLocation();
  const { organizationId } = useParams();

  const isEdit = !!organizationId;

  const basePath = isEdit
    ? `clientDetails/edit/${organizationId}`
    : "clientDetails/add";

  const steps = [
    {
      label: "Client Details",
      path: `${basePath}/details`,
    },
    {
      label: "Setup Client Zones",
      path: `${basePath}/zones`,
    },
  ];

  const currentPage = location.pathname.includes("/details")
    ? "details"
    : location.pathname.includes("/zones")
      ? "zones"
      : "products";

  /* ---------------- Child Validation ---------------- */

  const clientRef = useRef();

  const zoneRef = useRef();

  /* ---------------- Parent State ---------------- */

  const [loading, setLoading] = useState(false);
  const [zonesLoading, setZonesLoading] = useState(isEdit);

  const [clientData, setClientData] = useState({});

  const [zoneData, setZoneData] = useState([]);

  /* ---------------- Step ---------------- */

  const activeStep = steps.findIndex((step) =>
    location.pathname.endsWith(step.path),
  );

  /* ---------------- Edit Mode ---------------- */

  useEffect(() => {
    if (!organizationId) return;

    getOrganizationDetails();
  }, [organizationId]);

  const getOrganizationDetails = async () => {
    try {
      setLoading(true);
      setZonesLoading(true);

      const response = await axios.get(
        `${serverURL}/organization/view/${organizationId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (!response.data.success) return;

      const data = response.data;

      // Client Details
      setClientData(data.organizationDetail || {});

      // Baad me karenge
      setZoneData(data.regions || []);
      // setProductData(...)
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setZonesLoading(false);
    }
  };

  /* ---------------- Next ---------------- */

  const handleNext = async () => {
    let valid = true;

    switch (activeStep) {
      case 0:
        valid = await clientRef.current.validate();
        break;

      case 1:
        valid = await zoneRef.current.validate();
        break;

      default:
        break;
    }

    if (!valid) return;

    navigate(`../${steps[activeStep + 1].path}`);
  };

  /* ---------------- Previous ---------------- */

  const handleBack = () => {
    navigate(`../${steps[activeStep - 1].path}`);
  };

  /* ---------------- Final Submit ---------------- */

  // const handleSubmit = async () => {
  //   console.log(zoneData);
  //   const regions = zoneData.zones.map((zone) => ({
  //     regionName: zone.name,
  //     color: zone.color,
  //     states: zoneData.states
  //       .filter((state) => state.zoneId === zone.id)
  //       .map((state) => state.name),
  //   }));

  //   const payload = {
  //     organization: clientData,
  //     regions,
  //     rateCard: {
  //       ...productData,
  //       products: productData.products.map((item) => ({
  //         id: item.product,
  //         alias: item.alias,
  //         guidancePrice: item.guidancePrice,
  //         unit: item.unit,
  //         description: item.description,
  //       })),
  //     },
  //   };
  //   console.log(payload);

  //   try {
  //     setLoading(true);

  //     let response;

  //     if (isEdit) {
  //       response = await axios.put(
  //         `${serverURL}/organization/update/${organizationId}`,
  //         payload,
  //         {
  //           headers: {
  //             Authorization: `Bearer ${localStorage.getItem("token")}`,
  //           },
  //         },
  //       );
  //     } else {
  //       response = await axios.post(
  //         `${serverURL}/organization/create`,
  //         payload,
  //         {
  //           headers: {
  //             Authorization: `Bearer ${localStorage.getItem("token")}`,
  //           },
  //         },
  //       );
  //     }

  //     if (!response.data.success) {
  //       Swal.fire({
  //         icon: "error",
  //         text: response.data.message,
  //       });

  //       return;
  //     }

  //     Swal.fire({
  //       icon: "success",
  //       text: response.data.message,
  //     });

  //     // navigate("/clientDetails");
  //   } catch (error) {
  //     Swal.fire({
  //       icon: "error",
  //       text: error.response?.data?.message || "Something went wrong",
  //     });
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  const handleSubmit = async () => {
    try {
      if (currentPage === "zones" || !isEdit) {
        if (zoneRef.current && !zoneRef.current.validate()) return;
      }

      setLoading(true);

      let payload = {};

      if (isEdit) {
        // Edit only current section
        if (currentPage === "details") {
          payload = {
            organizationName: clientData.organizationName,
            gstin: clientData.gstin,
            address: clientData.address,
            city: clientData.city,
            state: clientData.state,
            pinCode: clientData.pinCode,
            sector: clientData.sector,
            domain: clientData.domain,
            brandLogo: clientData.brandLogo,
            isAnnualPartner: clientData.isAnnualPartner,
          };
          await axios.put(
            `${serverURL}/organization/update/${organizationId}`,
            payload,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            },
          );
        }

        if (currentPage === "zones") {
          payload = {
            organizationId,
            regions: zoneRef.current.getPayload(),
          };
          await axios.put(`${serverURL}/organization/region`, payload, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          });
          await getOrganizationDetails();
        }
      } else {
        // Create Client
        payload = {
          organization: clientData,
          regions: zoneRef.current.getPayload(),
        };

        await axios.post(`${serverURL}/organization/create`, payload, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
      }

      Swal.fire({
        icon: "success",
        text: isEdit ? "Updated Successfully" : "Created Successfully",
      });

      if (isEdit) {
        navigate(-1);
      } else {
        navigate("/adminPortal/clientDetails");
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        text: error.response?.data?.message || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#F5F7FA",
      }}
    >
      {!isEdit && (
        <Paper
          elevation={0}
          square
          sx={{
            px: 5,
            py: 4,
            bgcolor: "#fff",
            borderBottom: "1px solid #EAECF0",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: 24,
              fontWeight: 600,
              mb: isEdit ? 0 : 0,
              width: "150px",
            }}
          >
            {"Add Client"}
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="100%"
            py={2}
          >
            {steps.map((step, index) => {
              const isCompleted = activeStep > index;
              const isActive = activeStep === index;

              return (
                <React.Fragment key={step.path}>
                  <Box display="flex" alignItems="center">
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        border: "2px solid",
                        borderColor:
                          isCompleted || isActive ? "#101828" : "#D0D5DD",
                        bgcolor: isCompleted ? "#101828" : "#fff",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {isCompleted ? (
                        <Typography
                          sx={{
                            color: "#fff",
                            fontSize: 14,
                            fontWeight: 700,
                          }}
                        >
                          ✓
                        </Typography>
                      ) : isActive ? (
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            bgcolor: "#101828",
                          }}
                        />
                      ) : null}
                    </Box>

                    <Typography
                      sx={{
                        ml: 1.5,
                        fontWeight: isActive ? 600 : 500,
                        color: isCompleted || isActive ? "#101828" : "#98A2B3",
                      }}
                    >
                      {step.label}
                    </Typography>
                  </Box>

                  {index !== steps.length - 1 && (
                    <Box
                      sx={{
                        flex: 1,
                        maxWidth: 140,
                        mx: 3,
                        height: 2,
                        bgcolor: activeStep > index ? "#101828" : "#E4E7EC",
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        </Paper>
      )}

      <Box p={4}>
        <Routes>
          <Route index element={<Navigate to="details" replace />} />

          <Route
            path="details"
            element={
              <ClientOnboarding
                ref={clientRef}
                data={clientData}
                onChange={setClientData}
                isEdit={isEdit}
              />
            }
          />

          <Route
            path="zones"
            element={
              <ZoneManagement
                ref={zoneRef}
                data={zoneData}
                onChange={setZoneData}
                loading={zonesLoading}
              />
            }
          />
        </Routes>
      </Box>
      <Paper
        elevation={0}
        square
        sx={{
          px: 4,
          py: 2,
          display: "flex",
          justifyContent: isEdit ? "flex-end" : "space-between",
          borderTop: "1px solid #EAECF0",
          position: "sticky",
          bottom: 0,
          bgcolor: "#fff",
        }}
      >
        {!isEdit && (
          <div style={{ width: "auto" }}>
            <GlobalButton
              variant="primary"
              size="medium"
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              Previous
            </GlobalButton>
          </div>
        )}

        {isEdit ? (
          <div style={{ width: "auto" }}>
            <GlobalButton
              variant="primary"
              size="large"
              isLoading={loading}
              onClick={handleSubmit}
            >
              Update
            </GlobalButton>
          </div>
        ) : activeStep === steps.length - 1 ? (
          <div style={{ width: "auto" }}>
            <GlobalButton
              variant="primary"
              size="large"
              isLoading={loading}
              onClick={handleSubmit}
            >
              Submit
            </GlobalButton>
          </div>
        ) : (
          <div style={{ width: "auto" }}>
            <GlobalButton
              variant="primary"
              size="large"
              isLoading={loading}
              onClick={handleNext}
            >
              Next
            </GlobalButton>
          </div>
        )}
      </Paper>
    </Box>
  );
}
